import type { DataProxy } from 'apollo-cache';
import type { DocumentNode } from 'graphql';
import type { GraphQLTaggedNode } from 'relay-runtime';

import { toDocumentNode } from '@confluence/apollo-relay-interop/entry-points/toDocumentNode.ts';
import type { ResolvedCommentsQueryType } from '@confluence/comments-panel-queries/entry-points/ResolvedCommentsQuery.ts';
import type {
	ActiveCommentsPanelNode,
	ActiveCommentsQueryType,
} from '@confluence/comments-panel-queries/entry-points/ActiveCommentsQuery.ts';
import type { AnalyticsSource } from '@confluence/comments-util/entry-points/analytics.ts';
import { fg } from '@confluence/feature-gating';
import { expVal } from '@confluence/feature-experiments';
import type {
	ActiveInlineCommentsQueryType,
	TopLevelComment,
	CommentLocation,
	CommentReply,
} from '@confluence/inline-comments-queries/entry-points/ActiveInlineCommentsQuery.ts';
import type {
	CommentsPanelQueryType,
	CommentsPanelQuery_comments_nodes,
} from '@confluence/inline-comments-queries/entry-points/CommentsPanelQuery.ts';
import type { Logger } from '@confluence/logger';
import { PageMode, ContentStatus } from '@confluence/page-utils/entry-points/enums.ts';

// eslint-disable-next-line import/no-cycle
import {
	CommentType,
	FooterCommentsLoadingStateType as FooterCommentsLoadingStateEnum,
	// eslint-disable-next-line import/no-cycle
} from '../useCommentsData.tsx';
import type {
	CommentData,
	CommentsDataMap,
	FooterCommentsLoadingStateType,
} from '../useCommentsData.tsx';

type NormalizeCommentDataFn = (input: {
	comment: TopLevelComment;
	currentUserId: string;
	isOpen: boolean;
	type: CommentType;
}) => CommentData;

export const normalizeCommentData: NormalizeCommentDataFn = ({ comment, isOpen, type }) => {
	return {
		...comment,
		wasRemovedByAnotherUser: false,
		isOpen,
		type,
		replies: (comment.replies as CommentReply[])?.map((reply) => {
			return {
				...reply,
				reactionsSummary: reply.reactionsSummary ?? null,
				type,
				wasRemovedByAnotherUser: false,
			};
		}),
	};
};

export const updateCommentsDataState = ({
	data,
	pageMode,
	currentUserId,
	updateData,
	isOpen = true,
}: {
	data: ActiveInlineCommentsQueryType | ActiveCommentsQueryType | ResolvedCommentsQueryType;
	pageMode: PageMode;
	currentUserId: string;
	updateData: (newCommentsDataMap: CommentsDataMap) => void;
	isOpen: boolean;
}): void => {
	const inlineCommentThreadsToAdd: Record<string, CommentData> = {};
	const generalCommentThreadsToAdd: Record<string, CommentData> = {};

	const loadFooterComments = pageMode !== PageMode.EDIT;

	data?.comments?.nodes?.forEach((commentNode) => {
		// Filter out null values
		if (!commentNode) {
			return;
		}
		const comment = {
			...commentNode,
			reactionsSummary: commentNode?.reactionsSummary ?? null,
		} as TopLevelComment;
		// We only want top level comments
		if (comment && !comment.parentId) {
			if (
				comment.contentStatus === ContentStatus.DRAFT &&
				pageMode === PageMode.VIEW &&
				fg('confluence_frontend_content_wrapper')
			) {
				return;
			}

			const commentType = comment.location.type;

			if (commentType === 'INLINE') {
				const threadKey = (comment.location as CommentLocation).inlineMarkerRef;

				if (threadKey) {
					inlineCommentThreadsToAdd[threadKey] = normalizeCommentData({
						comment,
						currentUserId,
						isOpen,
						type: CommentType.INLINE,
					});
				}
			} else if (commentType === 'FOOTER' && loadFooterComments) {
				const threadKey = comment.id;

				generalCommentThreadsToAdd[threadKey] = normalizeCommentData({
					comment,
					currentUserId,
					isOpen,
					type: CommentType.GENERAL,
				});
			}
		}
	});

	updateData({
		inline: inlineCommentThreadsToAdd,
		general: generalCommentThreadsToAdd,
	});
};

export const parseHasNestedComments = (commentsDataMap: CommentsDataMap): boolean => {
	try {
		const allGeneralComments = Object.values(commentsDataMap.general);

		// To see if there are nested comments, we need to check whether there are any replies whose parentID is not equal to the parent Comment's ID
		// This is because commentsDataMap.general is a flat map of parent comment --> everything below it
		// But each comment has it's own parent id which may be different from it's top level parent
		return allGeneralComments.some((comment) => {
			return comment?.replies?.some((reply) => reply.parentId !== comment.id);
		});
	} catch {
		// Default to false if somehow something goes wrong
		return false;
	}
};

export const updateFooterCommentsLoadingState = (
	activeCommentsQueryResult: ActiveCommentsQueryType | undefined,
	updateFn: (state: FooterCommentsLoadingStateType) => void,
): void => {
	// This should only ever be called with data - however if it isn't we should update state to NOT_LOADED
	if (activeCommentsQueryResult?.comments?.totalCount === undefined) {
		updateFn(FooterCommentsLoadingStateEnum.NOT_LOADED);
		return;
	}

	const loadedCommentCount =
		activeCommentsQueryResult?.comments?.nodes?.reduce(
			(acc: number, comment: ActiveCommentsPanelNode) => {
				return acc + (comment?.replies?.length || 0) + 1; // return parent + reply count
			},
			0,
		) ?? 0;

	// The BE sometimes will give us mismatched numbers for complex nested scenarios, as long as > 0 call it good
	// This case is super rare (should be <<1% of pages), but if it significantly affects metrics will need another special case
	const allLoaded = loadedCommentCount === activeCommentsQueryResult?.comments?.totalCount;

	if (allLoaded) {
		updateFn(FooterCommentsLoadingStateEnum.FULLY_LOADED);
	} else {
		updateFn(FooterCommentsLoadingStateEnum.PARTIALLY_LOADED);
	}
};

export const findCommentById = (commentsDataMap: CommentsDataMap, commentId: string) => {
	// Check if our ID is one of the parent comments, if so return the parent comment
	const findParent = Object.values(commentsDataMap.general).find(
		(comment) =>
			comment.id === commentId || comment.replies?.some((reply) => reply.id === commentId),
	);

	if (!findParent) {
		return null;
	}

	if (findParent?.id === commentId) {
		return findParent;
	} else {
		return findParent.replies?.find((reply) => reply.id === commentId);
	}
};

// Returns the top level parent of a comment. If the comment Id supplied is not a reply, it'll return the comment in question
export const getTopLevelParentComment = (commentsDataMap: CommentsDataMap, commentId: string) => {
	return Object.values(commentsDataMap.general).find(
		(comment) =>
			comment.id === commentId || comment.replies?.some((reply) => reply.id === commentId),
	);
};

export const resolveCommentInDataMap = (
	commentsDataMap: CommentsDataMap,
	commentType: CommentType,
	threadKey: string,
	addNewCommentThreads: (commentsDataMap: CommentsDataMap) => void,
): void => {
	setCommentIsOpenInDataMap(false, commentsDataMap, commentType, threadKey, addNewCommentThreads);
};

export const reopenCommentInDataMap = (
	commentsDataMap: CommentsDataMap,
	commentType: CommentType,
	threadKey: string,
	addNewCommentThreads: (commentsDataMap: CommentsDataMap) => void,
): void => {
	setCommentIsOpenInDataMap(true, commentsDataMap, commentType, threadKey, addNewCommentThreads);
};

const setCommentIsOpenInDataMap = (
	isOpen: boolean,
	commentsDataMap: CommentsDataMap,
	commentType: CommentType,
	threadKey: string,
	addNewCommentThreads: (commentsDataMap: CommentsDataMap) => void,
): void => {
	const commentsDataMapClone = { ...commentsDataMap };
	const commentInMap = commentsDataMapClone[commentType][threadKey];
	if (commentInMap) {
		commentInMap.isOpen = isOpen;
		if (isOpen) {
			commentInMap.wasRemovedByAnotherUser = false;
		}

		// Handle both cases of when comment is Inline or General.
		// Only one of the following two properties will be present.
		const commentLocation = commentInMap.location;

		if (
			commentLocation &&
			'commentResolveProperties' in commentLocation &&
			commentLocation.commentResolveProperties
		) {
			(commentLocation.commentResolveProperties as any).resolved = !isOpen;
		}

		if (
			commentLocation &&
			'inlineResolveProperties' in commentLocation &&
			commentLocation.inlineResolveProperties &&
			// This helper function was extracted from
			// next/packages/comments-panel/src/components/Comment.tsx, where
			// this fg is required only when reopening a comment but not when
			// resolving. It's unclear why, but leaving it as is for now.
			// This can be cleaned up entirely after fg is rolled out.
			(!isOpen || fg('confluence_frontend_content_wrapper'))
		) {
			(commentLocation.inlineResolveProperties as any).resolved = !isOpen;
		}

		addNewCommentThreads(commentsDataMapClone);
	}
};

// Extracted from next/packages/comments-panel/src/components/Comment.tsx
// and next/packages/object-comments/src/components/ObjectComment.tsx
export const updateCommentsQueryCacheForResolve = ({
	cache,
	commentId,
	query,
	queryVariables,
	cacheUpdateFailed,
	setCacheUpdateFailed,
	source,
	logger,
}: {
	cache: DataProxy;
	commentId: string;
	query: GraphQLTaggedNode | DocumentNode;
	queryVariables: any;
	cacheUpdateFailed: boolean;
	setCacheUpdateFailed: (value: boolean) => void;
	source: AnalyticsSource;
	logger: Logger;
}): void => {
	// readQuery can still return null which complicates TS lint errors with optional chaining
	// once we move to v3 of the apollo client we can move to cache.modify
	try {
		let dataProxy = undefined;
		try {
			dataProxy = cache.readQuery<ActiveCommentsQueryType | CommentsPanelQueryType>({
				query: toDocumentNode(query),
				variables: queryVariables,
			});
		} catch (err) {
			// It's possible that the cache does not contain a matchign entry for `query`.
			// In this case, an error "Invariant Violation: Invariant Violation: 8"
			// is thrown, which we can safely ignore and skip this cache update.
			if (expVal('confluence_fe_resolve_comment_optimistic', 'isEnabled', false)) {
				logger.warn`Unable to read cache for ${source} resolve - ${err}`;
				return;
			} else {
				throw err;
			}
		}

		const commentsToWrite = Object.assign({}, dataProxy?.comments);
		let totalCount = commentsToWrite.totalCount || 0;
		const commentsList = commentsToWrite.nodes?.filter(
			(n: ActiveCommentsPanelNode | CommentsPanelQuery_comments_nodes) => n !== null,
		) as TopLevelComment[];

		const idxToRemove = commentsList.findIndex((c) => c.id === commentId);

		// Remove the newly resolved comment from the list if we can find it.
		if (idxToRemove !== -1) {
			commentsList.splice(idxToRemove, 1);
			totalCount--;
		}

		// Otherwise, no need to update the cache.
		// Merge with if block above after shipping this experiment.
		if (
			idxToRemove !== -1 ||
			!expVal('confluence_fe_resolve_comment_optimistic', 'isEnabled', false)
		) {
			cache.writeQuery<ActiveCommentsQueryType | CommentsPanelQueryType>({
				query: toDocumentNode(query),
				variables: queryVariables,
				data: {
					comments: {
						nodes: commentsList,
						totalCount,
						__typename: 'PaginatedCommentList',
					},
				},
			});
		}

		// cache updated successfuly
		// now check if previous cache update was failed
		if (cacheUpdateFailed) {
			setCacheUpdateFailed(false);
		}
	} catch (err) {
		logger.error`An error occurred when updating cache for ${source} resolve - ${err}`;
		setCacheUpdateFailed(true);
	}
};
